// AIDEV-NOTE: Orchestrates the Manage Programs save: POST → poll AutoRosterSync →
// fetch RosterEvent → classify errors. Inline effects handle the timeout and the
// no-RosterEvent edge case (sync finished Failed before FinishSyncWorker ran).
import { useEffect, useState } from 'react';
import Axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import { SaveErrorKind } from './SaveErrorBanner';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120_000; // 2 minutes
const IN_FLIGHT_STATUSES = new Set(['Scheduled', 'In Progress']);

const ERROR_KIND_PATTERNS: Array<{ kind: SaveErrorKind; pattern: RegExp }> = [
  { kind: 'program_excluded', pattern: /Remove program exclusions|program is excluded from rostering/i },
  { kind: 'insufficient_licenses', pattern: /license|insufficient/i },
];

const KIND_PRIORITY: SaveErrorKind[] = ['program_excluded', 'insufficient_licenses', 'generic'];

interface SyncStatusResponse {
  auto_roster_sync: { id: number; roster_event_id: number | null; status: string };
}

interface RosterEventRecord {
  errors?: string;
  extra_attr?: string;
  priority?: string;
  program_id?: number;
  uid?: string;
}

interface RosterEventResponse {
  roster_event: {
    archived?: { count: number; results?: Record<string, RosterEventRecord[]> };
    auto_roster_sync_id?: number;
    errors?: { count: number; results?: Record<string, RosterEventRecord[]> };
    id: number;
    warnings?: { count: number; results?: Record<string, RosterEventRecord[]> };
  };
}

interface PostResponse {
  sync_id: number;
}

export interface ProgramFailure {
  kind: SaveErrorKind;
  message: string;
  programId?: number;
}

interface UseManageProgramsSaveOptions {
  classroomId: number;
  classroomUid: string;
  editCounter: number;
  onPartialFailure?: () => void;
  onSuccess?: () => void;
  programsToLink: number[];
  programsToUnlink: Set<number>;
}

interface UseManageProgramsSaveResult {
  isSaving: boolean;
  programFailures: ProgramFailure[];
  save: () => void;
  saveError: SaveErrorKind | null;
}

const classifyError = (message: string): SaveErrorKind => (
  ERROR_KIND_PATTERNS.find(({ pattern }) => pattern.test(message))?.kind ?? 'generic'
);

const dominantKind = (failures: ProgramFailure[]): SaveErrorKind => (
  KIND_PRIORITY.find(kind => failures.some(f => f.kind === kind)) ?? 'generic'
);

const classroomFailuresForUid = (data: RosterEventResponse, classroomUid: string): ProgramFailure[] => {
  const seen = new Set<number>();

  return (data.roster_event.errors?.results?.classrooms ?? [])
    .filter(record => record.uid === classroomUid)
    .flatMap(record => (
      record.errors
        ? [{ kind: classifyError(record.errors), message: record.errors, programId: record.program_id }]
        : []
    ))
    .filter((failure) => {
      if (failure.programId === undefined) return true;
      if (seen.has(failure.programId)) return false;
      seen.add(failure.programId);
      return true;
    });
};

const useManageProgramsSave = ({
  classroomId,
  classroomUid,
  editCounter,
  onPartialFailure,
  onSuccess,
  programsToLink,
  programsToUnlink,
}: UseManageProgramsSaveOptions): UseManageProgramsSaveResult => {
  const [syncId, setSyncId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<SaveErrorKind | null>(null);
  const [programFailures, setProgramFailures] = useState<ProgramFailure[]>([]);

  const postMutation = useMutation<PostResponse, Error>({
    mutationFn: async () => {
      const response = await Axios.post<PostResponse>(
        Routes.plato_api_manage_programs_classroom_path(classroomId),
        { programs_to_link: programsToLink, programs_to_unlink: Array.from(programsToUnlink) },
      );
      return response.data;
    },
    onError: () => setSaveError('generic'),
    onSuccess: data => setSyncId(data.sync_id),
  });

  const syncQuery = useQuery<SyncStatusResponse, Error>({
    enabled: syncId !== null && saveError === null,
    onError: () => setSaveError('generic'),
    queryFn: async () => {
      const response = await Axios.get<SyncStatusResponse>(
        Routes.plato_api_auto_roster_sync_path(syncId!),
        { params: { only: ['id', 'status', 'roster_event_id'] } },
      );
      return response.data;
    },
    queryKey: ['autoRosterSyncStatus', syncId],
    refetchInterval: data => (
      data && !IN_FLIGHT_STATUSES.has(data.auto_roster_sync.status) ? false : POLL_INTERVAL_MS
    ),
  });

  const sync = syncQuery.data?.auto_roster_sync;
  const terminalStatus = sync && !IN_FLIGHT_STATUSES.has(sync.status) ? sync.status : null;
  const rosterEventId = terminalStatus ? sync!.roster_event_id : null;

  const resultsQuery = useQuery<RosterEventResponse, Error>({
    enabled: rosterEventId !== null && saveError === null,
    onError: () => setSaveError('generic'),
    onSuccess: (data) => {
      const failures = classroomFailuresForUid(data, classroomUid);
      if (failures.length > 0) {
        setProgramFailures(failures);
        setSaveError(dominantKind(failures));
        onPartialFailure?.();
      } else if (terminalStatus !== 'Finished') {
        setSaveError('generic');
      } else {
        onSuccess?.();
      }
    },
    queryFn: async () => {
      const response = await Axios.get<RosterEventResponse>(
        Routes.plato_api_roster_event_path(rosterEventId!),
      );
      return response.data;
    },
    queryKey: ['rosterEvent', rosterEventId],
  });

  // Safety net: bail if we never reach a terminal state within the budget
  // (e.g. Sidekiq is down — refetchInterval would otherwise poll forever).
  useEffect(() => {
    if (syncId === null) return undefined;
    const timer = setTimeout(() => setSaveError('generic'), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [syncId]);

  // A failed sync can finish without ever creating a RosterEvent (e.g. the
  // sync worker blew up before FinishSyncWorker ran). Nothing to fetch —
  // surface the generic banner.
  useEffect(() => {
    if (terminalStatus && rosterEventId === null && saveError === null) setSaveError('generic');
  }, [terminalStatus, rosterEventId, saveError]);

  useEffect(() => {
    setSaveError(null);
    setSyncId(null);
    setProgramFailures([]);
  }, [editCounter]);

  const isSaving = saveError === null && (
    postMutation.isLoading
    || (syncId !== null && terminalStatus === null)
    || resultsQuery.isFetching
  );

  const save = () => {
    setSaveError(null);
    setSyncId(null);
    setProgramFailures([]);
    postMutation.mutate();
  };

  return { isSaving, programFailures, save, saveError };
};

export default useManageProgramsSave;
